import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { compose } from 'ramda';
import { normalizeJssFields } from '_utils/helpers/jss';
import { Link } from '@sitecore-jss/sitecore-jss-react';

import { withEditMode, withEditModeNotice } from '_containers/BaseComponent';
import HeadingWithDescription from '_utils/components/Corporate/HeadingWithDescription';
import ShowMore from '_utils/components/Corporate/ShowMore';
import { getItemStyle } from './definitions';
import Item from './Item';
import NavItem from '../NavItem';
import Arrow from '_utils/icons/Arrow';
import External from '_utils/icons/External';
import {
  Container,
  List,
  ListItem,
  ShowMoreContainer,
  FooterLink,
  FooterLinkLabel,
  ArrowWrapper,
  FooterLinkContainer
} from './StyledRelatedContentList';
import { getLink, isValidLinkObject } from '_utils/helpers/link';
import { warnInDev } from '_utils/helpers/dev';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

const EditContainer = compose(withEditModeNotice)(styled.div``);
const EditFooterLink = compose(withEditModeNotice)(FooterLink);
const EditShowMoreContainer = compose(withEditModeNotice)(ShowMoreContainer);

function validateItem(item) {
  let errors = [];

  if (!item) {
    errors.push('invalid item');
  } else {
    if (!Boolean(item?.title)) {
      errors.push('title is missing');
    }
    if (!isValidLinkObject(getLink(item?.link))) {
      errors.push('link is missing');
    }
  }

  return errors.length ? errors : undefined;
}

function getInvalidItemMessage(item) {
  const errors = validateItem(item);
  if (!errors) {
    return '';
  }
  return `Item will not be displayed: ${errors.join(
    '; '
  )}. Edit the component's related item to change items.`;
}

function EditorItem({
  item,
  itemStyle,
  itemsToShow,
  numVisibleItems,
  editMode,
  isValidFooterLink
}) {
  const isVisible = itemsToShow.indexOf(item) > -1;
  const validationErrors = validateItem(item);

  let editProps = {};

  if (validationErrors !== undefined) {
    editProps = {
      noticeEnabled: validateItem(item) !== undefined,
      noticeLevel: 'warning',
      noticeMessage: getInvalidItemMessage(item)
    };
  } else if (!isVisible && isValidFooterLink) {
    editProps = {
      noticeEnabled: !isVisible,
      noticeLevel: 'warning',
      noticeMessage: `Item will be not be shown (showing first ${numVisibleItems} and footer link is set).`
    };
  } else {
    editProps = {
      noticeEnabled: !isVisible,
      noticeLevel: 'info',
      noticeMessage: `Item will be initially hidden (showing first ${numVisibleItems}).`
    };
  }

  return (
    <EditContainer {...editProps}>
      <Item {...item} itemStyle={itemStyle} editMode={editMode} />
    </EditContainer>
  );
}

EditorItem.propTypes = {
  item: PropTypes.object.isRequired,
  itemStyle: PropTypes.string.isRequired,
  itemsToShow: PropTypes.array.isRequired,
  numVisibleItems: PropTypes.number.isRequired,
  editMode: PropTypes.bool.isRequired,
  isValidFooterLink: PropTypes.bool.isRequired,
};

function RelatedContentList(props) {
  const { rendering, editMode, fieldsOverride, paramsOverride } = props;

  const [t] = useTranslation();

  const fields = fieldsOverride || rendering?.fields?.data?.datasource || rendering?.fields;
  const params = paramsOverride || rendering?.params;

  const normalized = normalizeJssFields(fields);

  let {
    heading,
    description,
    headingLevel,
    items,
    numVisibleItems,
    footerLink,
    footerLinkLabel
  } = normalized;

  if (!items) items = [];

  if (typeof numVisibleItems === 'object' && numVisibleItems.hasOwnProperty('value')) {
    numVisibleItems = numVisibleItems.value;
  }

  if (typeof numVisibleItems === 'string') {
    numVisibleItems = parseInt(numVisibleItems);
  }

  if (!numVisibleItems) {
    numVisibleItems = 4;
  }

  const editItems = items?.slice();

  items = items.filter((item) => validateItem(item) === undefined);

  if (!editMode && items.length !== editItems.length) {
    warnInDev(`RelatedContentList ignored ${editItems.length - items.length} invalid items`);
  }

  const hasItems = items && items.length;
  const itemStyle = getItemStyle(params, 'theme');

  const [canShowMore, setCanShowMore] = useState(items.length > numVisibleItems);
  const [showingMore, setShowingMore] = useState(false);

  const itemsToShow = showingMore ? items : items.slice(0, numVisibleItems);
  const evenItemsClass = items && items.length % 2 === 0 ? 'is-even-items' : '';
  const showingAllClass = !canShowMore || showingMore ? 'is-showing-all' : '';
  const singleItemClass = itemsToShow.length === 1 ? 'is-single-item ' : '';

  const footerLinkObj = getLink(footerLink?.value);

  const isValidFooterLink = isValidLinkObject(footerLinkObj);

  const isValidFooterLinkLabel = Boolean(footerLinkLabel?.value);

  let footerLinkNotice = {
    noticeEnabled: false,
    noticeLevel: 'info',
    noticeMessage: ''
  };

  if (isValidFooterLink) {
    if (!isValidFooterLinkLabel) {
      footerLinkNotice.noticeEnabled = true;
      footerLinkNotice.noticeLevel = 'warning';
      footerLinkNotice.noticeMessage = `Label not set. Link will be displayed as ${footerLinkObj.url}`;
    } else {
      footerLinkNotice.noticeEnabled = true;
      footerLinkNotice.noticeMessage = `${footerLinkObj.url}`;
    }
  }

  useEffect(() => {
    setCanShowMore(items.length > numVisibleItems && !showingMore);
  }, [items, showingMore, numVisibleItems]);

  if (!fields) {
    warnInDev('Related content list component requires data source to render');
    return (
      <EditContainer
        noticeEnabled
        noticeLevel="warning"
        noticeMessage={'Related content list component requires data source to render'}
      />
    );
  }

  if (!hasItems) {
    if (editMode) {
      return (
        <EditContainer
          noticeEnabled
          noticeLevel="warning"
          noticeMessage={'Related content list component has no items and will not be displayed'}
        />
      );
    } else {
      // warnInDev('Related content list component has no items and will not be displayed');
      return null;
    }
  }

  return (
    <Container>
      {Boolean(heading || description) && (
        <HeadingWithDescription
          headingField={heading}
          descriptionField={description}
          headingLevel={headingLevel}
        />
      )}
      <List className={`${evenItemsClass} ${showingAllClass}`} as="ul">
        {editMode &&
          editItems.map((item, index) => (
            <ListItem
              className={`${singleItemClass}`}
              key={item.id || index}
              as="li"
              $config={{ col: { xs: 12, lg: 6 } }}
            >
              <EditorItem
                item={item}
                itemStyle={itemStyle}
                itemsToShow={itemsToShow}
                numVisibleItems={numVisibleItems}
                editMode={editMode}
                isValidFooterLink={isValidFooterLink}
              />
            </ListItem>
          ))}
        {!editMode &&
          items.map((item, index) => (
            <ListItem
              className={`${singleItemClass} ${!itemsToShow.includes(item) ? 'is-hidden' : ''}`}
              key={item.id || index}
              as="li"
              $config={{ col: { xs: 12, lg: 6 } }}
            >
              <Item {...item} itemStyle={itemStyle} />
            </ListItem>
          ))}
      </List>
      {Boolean(!isValidFooterLink && canShowMore) && (
        <EditShowMoreContainer
          noticeEnabled
          noticeLevel="info"
          noticeMessage="Show more will display because there are hidden items and footer link is not set."
        >
          <ShowMore
            label={footerLinkLabel?.value || t('related-content-default-show-more-label')}
            title={t('related-content-accessible-more-items-label')}
            count={items.length - numVisibleItems}
            onClick={() => {
              setShowingMore(true);
              setCanShowMore(false);
            }}
          />
        </EditShowMoreContainer>
      )}
      {editMode && (
        <>
          <EditContainer
            noticeEnabled={footerLinkNotice.noticeEnabled}
            noticeLevel={footerLinkNotice.noticeLevel}
            noticeMessage={footerLinkNotice.noticeMessage}
          >
            <Link field={footerLink} />
          </EditContainer>

          <EditContainer
            noticeEnabled
            noticeLevel="info"
            noticeMessage={'Use this field to change link label'}
          >
            <FooterLinkLabel field={footerLinkLabel} tag="p" />
          </EditContainer>
        </>
      )}
      {!editMode && isValidFooterLink && (
        <FooterLinkContainer>
          <NavItem
            StyledLink={editMode ? EditFooterLink : FooterLink}
            link={footerLinkObj}
            className="hide-on-print"
            description={footerLinkLabel?.value}
          >
            <FooterLinkLabel
              field={isValidFooterLinkLabel ? footerLinkLabel : { value: footerLinkObj.url }}
            />
            <ArrowWrapper>
              {footerLinkObj.type !== 'external' && <Arrow ariaHidden={true} />}
              {footerLinkObj.type === 'external' && <External ariaHidden={true} />}
            </ArrowWrapper>
          </NavItem>
        </FooterLinkContainer>
      )}
    </Container>
  );
}

RelatedContentList.propTypes = {
  rendering: PropTypes.shape({
    fields: PropTypes.shape({
      data: PropTypes.shape({
        datasource: PropTypes.shape({
          items: PropTypes.arrayOf(PropTypes.object),
        }),
      }),
    }),
    params: PropTypes.object,
  }),
  editMode: PropTypes.bool,
  fieldsOverride: PropTypes.object,
  paramsOverride: PropTypes.object,
};

export default compose(withEditMode)(RelatedContentList);
